import React, { useMemo, useState } from "react";
import { DownloadResultsService } from "@application/useCases";
import { FileMetadata } from "@domain/schemas/BI";
import { triggerArtifactDownloads } from "@utils/triggerArtifactDownloads";
import { ArtifactDownloadDialog } from "./ArtifactDownloadDialog";

type DownloadTarget = {
  title: string;
  loader: () => Promise<FileMetadata[]>;
  downloader: (selectedFiles: string[]) => Promise<string>;
};

type UseArtifactDownloadDialogProperties = {
  downloadResultsService: DownloadResultsService;
};

export const useArtifactDownloadDialog = ({ downloadResultsService }: UseArtifactDownloadDialogProperties) => {
  const [target, setTarget] = useState<DownloadTarget | null>(null);
  const [files, setFiles] = useState<FileMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | null>(null);

  const openDialog = async (nextTarget: DownloadTarget) => {
    setTarget(nextTarget);
    setFiles([]);
    setErrorMessage(null);
    setSubmitErrorMessage(null);
    setIsLoading(true);

    try {
      const availableFiles = await nextTarget.loader();
      setFiles(availableFiles);
      if (availableFiles.length === 0) {
        setErrorMessage("No downloadable files were found.");
      }
    } catch {
      setErrorMessage("Failed to load downloadable files.");
    } finally {
      setIsLoading(false);
    }
  };

  const closeDialog = () => {
    setTarget(null);
    setFiles([]);
    setErrorMessage(null);
    setSubmitErrorMessage(null);
    setIsLoading(false);
  };

  const dialog = useMemo(() => {
    if (!target) {
      return null;
    }

    return (
      <ArtifactDownloadDialog
        title={target.title}
        files={files}
        isLoading={isLoading}
        errorMessage={errorMessage}
        submitErrorMessage={submitErrorMessage}
        onClose={closeDialog}
        onDownload={async (selectedFiles) => {
          setSubmitErrorMessage(null);
          try {
            const downloadUrl = await target.downloader(selectedFiles);
            const startedDownloads = triggerArtifactDownloads(downloadUrl);

            // A successful response with no URLs must not look like a completed
            // download; closing here would silently report success for zero files.
            if (startedDownloads === 0) {
              setSubmitErrorMessage("No files were available to download.");
              return;
            }

            closeDialog();
          } catch {
            setSubmitErrorMessage("Failed to start download.");
          }
        }}
      />
    );
  }, [errorMessage, files, isLoading, submitErrorMessage, target]);

  return {
    artifactDownloadDialog: dialog,
    openJobDownloadDialog: async (jobId: string) =>
      openDialog({
        title: "Download job results",
        loader: () => downloadResultsService.listJobResultFiles(jobId),
        downloader: (selectedFiles) => downloadResultsService.getDownloadURL(jobId, selectedFiles),
      }),
    openStepDownloadDialog: async (jobId: string, jobPipelineStepId: string) =>
      openDialog({
        title: "Download step artifacts",
        loader: () => downloadResultsService.listStepArtifactFiles(jobId, jobPipelineStepId),
        downloader: (selectedFiles) => downloadResultsService.getStepDownloadURL(jobId, jobPipelineStepId, selectedFiles),
      }),
    openBatchDownloadDialog: async (jobId: string, jobPipelineStepId: string, batchId: string) =>
      openDialog({
        title: "Download batch artifacts",
        loader: () => downloadResultsService.listBatchArtifactFiles(jobId, jobPipelineStepId, batchId),
        downloader: (selectedFiles) => downloadResultsService.getBatchDownloadURL(jobId, jobPipelineStepId, batchId, selectedFiles),
      }),
  };
};
